import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Truck,
  ListPlus,
  Search,
  Copy,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STATUS_LABEL, STATUS_PROGRESS } from "@/lib/constants";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";

const STATUS_OPTIONS = [
  { v: "sourced", l: "Sourced" },
  { v: "processing", l: "Processing" },
  { v: "in_transit", l: "In Transit" },
  { v: "customs", l: "Customs Clearance" },
  { v: "out_for_delivery", l: "Out for Delivery" },
  { v: "delivered", l: "Delivered" },
  { v: "delayed", l: "Delayed" },
];

const EMPTY = {
  tracking_id: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  product_summary: "",
  reference_no: "",
  container_no: "",
  vessel_or_flight: "",
  origin: "",
  destination: "",
  current_status: "sourced",
  current_location: "",
  eta: "",
  eta_start: "",
  eta_end: "",
  progress_percent: STATUS_PROGRESS.sourced,
  carrier: "",
  package_count: "",
  total_weight: "",
  incoterm: "",
  status_detail: "",
  delay_reason: "",
  notes: "",
  receiver_name: "",
  delivered_at: "",
  delivery_proof_url: "",
  notifications_email_enabled: true,
  notifications_sms_enabled: false,
  notifications_whatsapp_enabled: false,
  events: [],
};

const EMPTY_EVENT = { location: "", status: "in_transit", note: "" };

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminTracking() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [eventOpen, setEventOpen] = useState(null); // shipment id
  const [event, setEvent] = useState(EMPTY_EVENT);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const setStatus = (status) => {
    setForm((prev) => ({
      ...prev,
      current_status: status,
      progress_percent:
        prev.progress_percent === STATUS_PROGRESS[prev.current_status]
          ? STATUS_PROGRESS[status]
          : prev.progress_percent,
    }));
  };

  const { data: shipments = [], isLoading } = useQuery({
    queryKey: ["admin-shipments"],
    queryFn: async () => (await api.get("/admin/shipments")).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const url = editing ? `/admin/shipments/${editing.id}` : "/admin/shipments";
      const method = editing ? "put" : "post";
      return (await api[method](url, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shipments"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast.success(editing ? "Shipment updated" : "Shipment created");
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/shipments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shipments"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast.success("Shipment deleted");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const addEventMut = useMutation({
    mutationFn: async ({ sid, body }) => (await api.post(`/admin/shipments/${sid}/events`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shipments"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setEventOpen(null);
      setEvent(EMPTY_EVENT);
      toast.success("Event added");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s, events: s.events || [] });
    setOpen(true);
  };

  const submit = (e) => {
    e.preventDefault();
    saveMut.mutate(form);
  };

  const filteredShipments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return shipments.filter((s) => {
      const matchesStatus = statusFilter === "all" || s.current_status === statusFilter;
      const haystack = [
        s.tracking_id,
        s.customer_name,
        s.customer_email,
        s.customer_phone,
        s.reference_no,
        s.origin,
        s.destination,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [shipments, searchTerm, statusFilter]);

  const stats = useMemo(
    () => ({
      total: shipments.length,
      active: shipments.filter((s) => s.current_status !== "delivered").length,
      delivered: shipments.filter((s) => s.current_status === "delivered").length,
      delayed: shipments.filter((s) => s.current_status === "delayed").length,
    }),
    [shipments]
  );

  return (
    <div data-testid="admin-tracking">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <Truck className="h-3.5 w-3.5" /> Logistics
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Tracking Manager
          </h1>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="add-shipment-button">
          <Plus className="h-4 w-4" /> New Shipment
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Total Shipments</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.total}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Active</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.active}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Delivered</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.delivered}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Delayed</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-red-600">{stats.delayed}</div>
        </div>
      </div>

      <div className="rsi-card p-5 mb-6">
        <div className="grid lg:grid-cols-[1.4fr,0.8fr] gap-4">
          <div>
            <Label>Search shipments</Label>
            <div className="mt-1.5 relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Tracking ID, customer, reference, route..."
                className="pl-10"
                data-testid="tracking-admin-search"
              />
            </div>
          </div>
          <div>
            <Label>Status filter</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-1.5" data-testid="tracking-admin-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="rsi-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rsi-bg-subtle)] text-left">
              <tr>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Tracking ID</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Customer</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Route</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Status</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">ETA</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={6} className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : filteredShipments.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-gray-500">No shipments yet.</td></tr>
              ) : (
                filteredShipments.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50" data-testid={`shipment-row-${s.tracking_id}`}>
                    <td className="p-4 font-mono-plex font-semibold text-[var(--rsi-navy)]">{s.tracking_id}</td>
                    <td className="p-4">
                      <div className="font-medium text-[var(--rsi-navy)]">{s.customer_name}</div>
                      <div className="text-xs text-gray-500">{s.customer_email || s.customer_phone || "—"}</div>
                    </td>
                    <td className="p-4 text-gray-600">
                      <div className="text-xs">{s.origin || "—"}</div>
                      <div className="text-xs text-gray-400">→ {s.destination || "—"}</div>
                    </td>
                    <td className="p-4">
                      <span className="badge-purple">{STATUS_LABEL[s.current_status]}</span>
                      <div className="text-xs text-gray-500 mt-1">{s.progress_percent || STATUS_PROGRESS[s.current_status] || 0}% complete</div>
                    </td>
                    <td className="p-4 font-mono-plex text-xs text-gray-600">{s.eta_start && s.eta_end ? `${s.eta_start} - ${s.eta_end}` : s.eta || "—"}</td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => { setEventOpen(s.id); setEvent(EMPTY_EVENT); }}
                          className="h-9 w-9 rounded-lg grid place-items-center hover:bg-[var(--rsi-purple-tint)] text-gray-600 hover:text-[var(--rsi-purple)]"
                          title="Add event"
                          data-testid={`add-event-${s.tracking_id}`}
                        >
                          <ListPlus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(s)}
                          className="h-9 w-9 rounded-lg grid place-items-center hover:bg-[var(--rsi-purple-tint)] text-gray-600 hover:text-[var(--rsi-purple)]"
                          data-testid={`edit-shipment-${s.tracking_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () => {
                            const url = `${window.location.origin}/track?id=${encodeURIComponent(s.tracking_id)}`;
                            try {
                              await navigator.clipboard.writeText(url);
                              toast.success("Tracking link copied");
                            } catch {
                              toast.error("Could not copy tracking link");
                            }
                          }}
                          className="h-9 w-9 rounded-lg grid place-items-center hover:bg-[var(--rsi-purple-tint)] text-gray-600 hover:text-[var(--rsi-purple)]"
                          title="Copy tracking link"
                          data-testid={`copy-link-${s.tracking_id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete ${s.tracking_id}?`)) delMut.mutate(s.id);
                          }}
                          className="h-9 w-9 rounded-lg grid place-items-center hover:bg-red-50 text-gray-600 hover:text-red-600"
                          data-testid={`delete-shipment-${s.tracking_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="shipment-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              {editing ? "Edit Shipment" : "New Shipment"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid gap-4 mt-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Tracking ID *</Label>
                <Input
                  required
                  placeholder="e.g. RSI-2026-0001"
                  value={form.tracking_id}
                  onChange={(e) => setForm({ ...form, tracking_id: e.target.value.toUpperCase() })}
                  className="mt-1.5 font-mono-plex"
                  data-testid="ship-form-id"
                />
              </div>
              <div>
                <Label>Carrier</Label>
                <Input
                  value={form.carrier}
                  onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Customer Phone</Label>
                <Input
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  required
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Order / Reference No.</Label>
                <Input
                  value={form.reference_no}
                  onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Container No.</Label>
                <Input
                  value={form.container_no}
                  onChange={(e) => setForm({ ...form, container_no: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Vessel / Flight</Label>
                <Input
                  value={form.vessel_or_flight}
                  onChange={(e) => setForm({ ...form, vessel_or_flight: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Cargo Summary</Label>
              <Input
                value={form.product_summary}
                onChange={(e) => setForm({ ...form, product_summary: e.target.value })}
                placeholder="e.g. 5,000 yards Premium Cotton Knit Fabric"
                className="mt-1.5"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Origin</Label>
                <Input
                  value={form.origin}
                  onChange={(e) => setForm({ ...form, origin: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Destination</Label>
                <Input
                  value={form.destination}
                  onChange={(e) => setForm({ ...form, destination: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Current Status</Label>
                <Select
                  value={form.current_status}
                  onValueChange={setStatus}
                >
                  <SelectTrigger className="mt-1.5" data-testid="ship-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Current Location</Label>
                <Input
                  value={form.current_location}
                  onChange={(e) => setForm({ ...form, current_location: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>ETA (Legacy single date)</Label>
                <div className="mt-1.5">
                  <DatePicker
                    value={form.eta}
                    onChange={(d) => setForm({ ...form, eta: d })}
                    placeholder="Select ETA"
                    testId="ship-form-eta"
                  />
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>ETA Start</Label>
                <div className="mt-1.5">
                  <DatePicker
                    value={form.eta_start}
                    onChange={(d) => setForm({ ...form, eta_start: d })}
                    placeholder="Start date"
                  />
                </div>
              </div>
              <div>
                <Label>ETA End</Label>
                <div className="mt-1.5">
                  <DatePicker
                    value={form.eta_end}
                    onChange={(d) => setForm({ ...form, eta_end: d })}
                    placeholder="End date"
                  />
                </div>
              </div>
              <div>
                <Label>Progress %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.progress_percent}
                  onChange={(e) => setForm({ ...form, progress_percent: Number(e.target.value || 0) })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Packages</Label>
                <Input
                  value={form.package_count}
                  onChange={(e) => setForm({ ...form, package_count: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Total Weight</Label>
                <Input
                  value={form.total_weight}
                  onChange={(e) => setForm({ ...form, total_weight: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Incoterm</Label>
                <Input
                  value={form.incoterm}
                  onChange={(e) => setForm({ ...form, incoterm: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Status Detail</Label>
                <Textarea
                  rows={2}
                  value={form.status_detail}
                  onChange={(e) => setForm({ ...form, status_detail: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Delay Reason</Label>
                <Textarea
                  rows={2}
                  value={form.delay_reason}
                  onChange={(e) => setForm({ ...form, delay_reason: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Receiver Name</Label>
                <Input
                  value={form.receiver_name}
                  onChange={(e) => setForm({ ...form, receiver_name: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Delivered At</Label>
                <Input
                  value={form.delivered_at}
                  onChange={(e) => setForm({ ...form, delivered_at: e.target.value })}
                  className="mt-1.5"
                  placeholder="ISO datetime or date"
                />
              </div>
              <div>
                <Label>Delivery Proof URL</Label>
                <Input
                  value={form.delivery_proof_url}
                  onChange={(e) => setForm({ ...form, delivery_proof_url: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <Label className="text-[var(--rsi-navy)]">Customer notifications</Label>
              <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.notifications_email_enabled}
                    onChange={(e) => setForm({ ...form, notifications_email_enabled: e.target.checked })}
                  />
                  Email updates
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.notifications_sms_enabled}
                    onChange={(e) => setForm({ ...form, notifications_sms_enabled: e.target.checked })}
                  />
                  SMS updates
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.notifications_whatsapp_enabled}
                    onChange={(e) => setForm({ ...form, notifications_whatsapp_enabled: e.target.checked })}
                  />
                  WhatsApp updates
                </label>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5"
              />
            </div>

            {editing && form.events?.length > 0 && (
              <div>
                <Label>Event log ({form.events.length})</Label>
                <div className="mt-2 rounded-lg border border-gray-200 max-h-48 overflow-y-auto">
                  {form.events.map((ev, i) => (
                    <div key={i} className="p-3 border-b border-gray-100 last:border-b-0 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{STATUS_LABEL[ev.status]} — {ev.location}</span>
                        <span className="font-mono-plex text-gray-500">{formatDate(ev.timestamp)}</span>
                      </div>
                      {ev.note && <div className="text-gray-600 mt-1">{ev.note}</div>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Use the &quot;Add Event&quot; button on the shipment row to append a new event.</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              <button
                type="submit"
                disabled={saveMut.isPending}
                className="btn-primary disabled:opacity-60"
                data-testid="ship-form-submit"
              >
                {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing ? "Save changes" : "Create shipment"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={!!eventOpen} onOpenChange={(v) => !v && setEventOpen(null)}>
        <DialogContent className="bg-white" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              Add tracking event
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 mt-4">
            <div>
              <Label>Status</Label>
              <Select value={event.status} onValueChange={(v) => setEvent({ ...event, status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location *</Label>
              <Input
                value={event.location}
                onChange={(e) => setEvent({ ...event, location: e.target.value })}
                className="mt-1.5"
                data-testid="event-form-location"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                rows={3}
                value={event.note}
                onChange={(e) => setEvent({ ...event, note: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button onClick={() => setEventOpen(null)} className="btn-ghost">Cancel</button>
              <button
                onClick={() => {
                  if (!event.location) return toast.error("Location required");
                  addEventMut.mutate({ sid: eventOpen, body: event });
                }}
                disabled={addEventMut.isPending}
                className="btn-primary disabled:opacity-60"
                data-testid="event-form-submit"
              >
                {addEventMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Add Event
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
